<div class="card position-relative">
  @if (isLoading) {
    <loader [config]="{ type: 'spinner', overlay: true, size: 'md' }"></loader>
  }

  <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
    <div class="d-flex align-items-center gap-2">
      <h3 class="card-title mb-0">Scan Detail</h3>
      @if (scan) {
        <span class="badge bg-primary-subtle text-primary scan-id-badge">#{{ scan.scanId }}</span>
      }
    </div>
    @if (scan?.hasReport) {
      <button type="button" class="btn btn-primary" (click)="downloadReport()" [disabled]="isDownloadingReport">
        @if (isDownloadingReport) {
          <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        } @else {
          <i class="fa fa-file-pdf me-1"></i>
        }
        Download PDF report
      </button>
    }
  </div>

  <div class="card-body">
    <regulator-context-box [items]="contextItems" (back)="goBack()" />

    @if (scan) {
      <div class="row g-3 mb-4">
        <div class="col-xl col-sm-6">
          <div class="scan-indicator">
            <span class="scan-indicator-label">Status</span>
            <span [class]="statusBadgeClass(scan.status) + ' scan-indicator-badge'">{{ statusLabel(scan) }}</span>
          </div>
        </div>
        <div class="col-xl col-sm-6">
          <div class="scan-indicator">
            <span class="scan-indicator-label">Verdict</span>
            @if (complianceLabel(scan.isCompliant); as verdict) {
              <span [class]="complianceBadgeClass(scan.isCompliant) + ' scan-indicator-badge'">{{ verdict }}</span>
            } @else {
              <span class="scan-indicator-value">-</span>
            }
          </div>
        </div>
        <div class="col-xl col-sm-6">
          <div class="scan-indicator">
            <span class="scan-indicator-label">Completed</span>
            <span class="scan-indicator-value">{{ formatDateTime(scan.completedAt) }}</span>
          </div>
        </div>
        <div class="col-xl col-sm-6">
          <div class="scan-indicator">
            <span class="scan-indicator-label">Trigger</span>
            <span class="scan-indicator-value">{{ triggerTypeLabel(scan.triggerType) }}</span>
          </div>
        </div>
        <div class="col-xl-4 col-sm-12">
          <div class="scan-indicator">
            <span class="scan-indicator-label">Final URL</span>
            @if (scan.finalUrl) {
              <a class="scan-indicator-link text-truncate" [href]="toHref(scan.finalUrl)" target="_blank" rel="noopener noreferrer">{{ scan.finalUrl }}</a>
            } @else {
              <span class="scan-indicator-value">-</span>
            }
          </div>
        </div>
      </div>

      @if (scan.status === 'FAILED' && scan.errorMessage) {
        <div class="alert alert-danger d-flex align-items-start gap-2" role="alert">
          <i class="fa fa-exclamation-triangle mt-1"></i>
          <div>
            <strong>Scan failed.</strong>
            <div class="scan-error-message">{{ scan.errorMessage }}</div>
          </div>
        </div>
      }

      <div class="row g-4">
        <div class="col-xl-7">
          <div class="scan-panel h-100">
            <div class="scan-panel-header">
              <h4 class="scan-panel-title"><i class="bi bi-list-check me-2"></i>Checks</h4>
            </div>
            @if (checks.length === 0) {
              <p class="text-muted p-3 mb-0">No checks available for this scan.</p>
            } @else {
              <div class="table-responsive">
                <table class="table table-sm align-middle mb-0 scan-checks-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Key</th>
                      <th style="width: 110px">Result</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (check of checks; track check.key) {
                      <tr>
                        <td class="fw-semibold">{{ check.name }}</td>
                        <td><code>{{ check.key }}</code></td>
                        <td><span [class]="checkBadgeClass(check.passed)">{{ checkLabel(check.passed) }}</span></td>
                        <td class="scan-check-message">{{ check.message || '-' }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        </div>

        <div class="col-xl-5">
          <div class="scan-panel h-100">
            <div class="scan-panel-header">
              <h4 class="scan-panel-title"><i class="bi bi-image me-2"></i>Screenshot</h4>
              @if (screenshotUrl) {
                <button type="button" class="btn btn-sm btn-outline-primary" (click)="openScreenshotFullSize()">
                  Open full size<i class="bi bi-box-arrow-up-right ms-1"></i>
                </button>
              }
            </div>
            @if (screenshotUrl) {
              <button type="button" class="scan-screenshot-button" (click)="openScreenshotFullSize()" title="Open full size">
                <img class="scan-screenshot" [src]="screenshotUrl" alt="Website screenshot" />
              </button>
            } @else {
              <div class="scan-screenshot-empty">
                @if (isLoadingScreenshot) {
                  <span class="spinner-border text-primary" role="status"></span>
                } @else {
                  <i class="bi bi-image"></i>
                  <p class="mb-0">{{ screenshotError ? 'Unable to load the screenshot' : 'Screenshot not available' }}</p>
                }
              </div>
            }
          </div>
        </div>
      </div>

      <div class="scan-panel mt-4">
        <div class="scan-panel-header">
          <h4 class="scan-panel-title"><i class="bi bi-code-slash me-2"></i>Raw payload</h4>
          <button type="button" class="btn btn-sm btn-outline-secondary" (click)="toggleRawPayload()" [attr.aria-expanded]="showRawPayload">
            {{ showRawPayload ? 'Hide' : 'Show' }}
            <i class="bi ms-1" [class.bi-chevron-up]="showRawPayload" [class.bi-chevron-down]="!showRawPayload"></i>
          </button>
        </div>
        @if (showRawPayload) {
          @if (formattedPayload) {
            <pre class="scan-raw-payload mb-0">{{ formattedPayload }}</pre>
          } @else {
            <p class="text-muted p-3 mb-0">No payload available.</p>
          }
        }
      </div>
    }
  </div>
</div>
